import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  DatePicker,
  Select,
  Typography,
  Space,
  Tag,
  Drawer,
  Divider,
  Button,
  Form,
  List,
  Avatar,
  Image,
  Alert,
  Row,
  Col,
  Upload,
  message,
  Statistic,
  Card,
} from "antd";

import dayjs from "dayjs";
import axios from "axios";

import { EditOutlined, SearchOutlined, UploadOutlined } from "@ant-design/icons";

import constants from "../constants";
import { useAuth } from "../contexts/authContext";

// TODO: fetch the counsellor id through token and update the api in backend to fetch counsellor id from token

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const DescriptionItem = ({ title, content }) => (
  <div className="site-description-item-profile-wrapper" style={{ padding: "5px 0", lineHeight: "20px" }}>
    <span style={{ fontWeight: "bold" }}>{title}</span>: {content || "N/A"}
  </div>
);

const AllCounellor = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drawerData, setDrawerData] = useState(false);

  const [form] = Form.useForm();

  const [filters, setFilters] = useState({
    name: "",
    phone: "",
    school: "",
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Debounce only the inputs; selects/date will also be debounced uniformly for simplicity
  const debouncedFilters = {
    name: useDebouncedValue(filters.name, 400),
    phone: useDebouncedValue(filters.phone, 400),
    school: useDebouncedValue(filters.school, 400),
  };

  const query = useMemo(() => ({ ...debouncedFilters, page, limit }), [debouncedFilters.name, debouncedFilters.phone, debouncedFilters.school, page, limit]);

  const { getToken } = useAuth();

  const fetchLeadsFromServer = async (query) => {
    const querySanitized = [`page=${query.page}`, `limit=${query.limit}`];
    delete query.page;
    delete query.limit;
    Object.keys(query).forEach((key) => {
      if (query[key]) {
        querySanitized.push(`${key}:cnts=${query[key]}`);
      }
    });

    try {
      const token = await getToken();
      const headers = {
        authorization: `bearer ${token}`,
      };

      const url = `${constants.NEW_DOMAIN}/api/v1/channelPartner/all-partners?${querySanitized.join("&")}`;
      const res = await axios.get(url, {
        headers,
      });
      //calculate size for pagination
      const len = res.data?.data?.length || 0;
      const size = len === query.limit ? query.limit * query.page + 1 : len;

      console.log(res);

      return {
        data: res.data?.data,
        total: size,
        page: query.page,
        limit: query.limit,
      };
    } catch (error) {
      throw new Error(error?.message || "something went wrong , try again later");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    fetchLeadsFromServer(query)
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Failed to load leads");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const handleInputChange = (key) => (e) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const columns = [
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Name</span>
          <Input placeholder="Filter name" value={filters.name} onChange={handleInputChange("name")} allowClear size="small" />
        </Space>
      ),
      dataIndex: "name",
      key: "name",
      render: (text) => <Typography.Text>{text}</Typography.Text>,
    },
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Phone</span>
          <Input placeholder="Filter phone" value={filters.phone} onChange={handleInputChange("phone")} allowClear size="small" />
        </Space>
      ),
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Email</span>
          <Input placeholder="Filter school" value={filters.email} onChange={handleInputChange("email")} allowClear size="small" />
        </Space>
      ),
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      render: (email) => <small>{email}</small>,
    },
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Joined On</span>
        </Space>
      ),
      dataIndex: "joiningDate",
      key: "joiningDate",
      render: (text) => (text ? dayjs(text).format("DD-MMM-YYYY") : "----------"),
    },

    {
      title: "More Details",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <a
          onClick={() => {
            setDrawerData(record);
          }}
        >
          More Details <SearchOutlined />
        </a>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    const { current, limit: newSize } = pagination;
    if (newSize !== limit) {
      setLimit(newSize);
    }
    setPage(current || 1);
  };

  return (
    <div style={{ padding: "12px" }}>
      {/* <AddNewLead
        updateRow={(record) => {
          setRows((prev) => [record, ...prev]);
        }}
      /> */}
      <Typography.Title level={3} mark>
        All Counsellors
      </Typography.Title>
      {error ? <Alert style={{ margin: "7px 0" }} message="Error" description={error} type="error" showIcon closable /> : null}

      <Drawer
        title="Partner Details"
        closable={{ "aria-label": "Close Button" }}
        size="large"
        onClose={() => {
          setDrawerData(false);
          form.resetFields();
        }}
        destroyOnClose
        afterOpenChange={(visible) => {
          if (visible) {
            form.setFieldsValue({ ...drawerData, followupDate: drawerData.followupDate ? dayjs(drawerData.followupDate) : null, id: drawerData._id });
          } else {
            form.resetFields();
          }
        }}
        open={drawerData}
      >
        <h1 className="site-description-item-profile-p" style={{ marginBottom: 24 }}>
          {drawerData.name}'s Details
        </h1>

        <Row>
          <Col span={24}>
            <DescriptionItem title="Name" content={drawerData.name} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <DescriptionItem title="Phone" content={drawerData.phone} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <DescriptionItem title="Email" content={drawerData.email} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <DescriptionItem title="Joined On " content={dayjs(drawerData.joiningDate).format("DD MMM YYYY")} />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <DescriptionItem title="Expiring On " content={dayjs(drawerData.tenureExpiry).format("DD MMM YYYY")} />
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
          <Col span={8}>
            <Card variant="borderless">
              <Statistic title="Total Commision Earned" value={drawerData.totalCommisionEarned} precision={2} valueStyle={{ color: "#3f8600" }} prefix="₹" />
            </Card>
          </Col>
          <Col span={8}>
            <Card variant="borderless">
              <Statistic title="Commision Pending" value={drawerData.commissionPending} precision={2} valueStyle={{ color: "#971eff" }} prefix="₹" />
            </Card>
          </Col>
          <Col span={8}>
            <Card variant="borderless">
              <Statistic title="Total Users" value={drawerData.totalActiveUsers} valueStyle={{ color: "red" }} />
            </Card>
          </Col>
          <Col span={8}>
            <Card variant="borderless">
              <Statistic title="Users in this Tenure" value={drawerData.tenureActiveUsers} valueStyle={{ color: "red" }} />
            </Card>
          </Col>
        </Row>
      </Drawer>
      <Table
        rowKey="_id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ current: page, limit, total, showSizeChanger: true }}
        onChange={handleTableChange}
        scroll={{ x: true }}
      />
    </div>
  );
};

export default AllCounellor;
